var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { object } from '../../utils/serializr/serializr';
import { Entity } from '../entity/Entity';
import { ActorStore } from './modules/ActorStore';
import { ActorBundles } from './modules/ActorBundles';
import { ActorRenderer } from './modules/ActorRenderer';
import { ActorEvents } from './modules/ActorEvents';
import { ActorMedia } from './modules/ActorMedia';
import { ActorInfo } from './modules/ActorInfo';
import { ActorAspects, ensuredAspectProxy } from './modules/ActorAspects';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { ActorInteract } from './modules/ActorInteract';
import { ActorMounted } from './modules/ActorMounted';
import { ActorImport } from './modules/ActorImport';
import { ActorTraits } from './modules/ActorTraits';
import { ImportingTree } from '../entity/tree/importingTree/ImportingTree';
import { ExportingTree } from '../entity/tree/bidderTree/ExportingTree';
import { ActorAttachedTraits } from './modules/ActorAttachedTraits';
import { ActorContext } from '../context/ActorContext';
import { withDefault } from '../../utils/serializr/schema/middle/withDefault';
import { ActorContent } from './modules/ActorContent';
import { BidderTreeNode } from '../entity/tree/bidderTree/BidderTreeNode';
import { ImportingTreeNode } from '../entity/tree/importingTree/ImportingTreeNode';
import { strip } from '../../utils/serializr/schema/inner/strip';
import { runtime } from '../runtime/Runtime';
import { ActorAudio } from './modules/ActorAudio';
export class Actor extends Entity {
    actorStore_;
    get importingChildren() {
        return [...super.importingChildren, ...this.actorBundles.activeBundles];
    }
    get exportingChildren() {
        if (this.context.app.appInfo.isAppInEditBuild) {
        }
        return [
            ...super.exportingChildren,
            // ...this.actorTraits.allOwnTraitsThatHaveInnerTraits,
            ...this.actorAttachedTraits.allAttachedActorTraitsDeep, // allAttachedTraitsDeep bad performance
        ];
    }
    get actorStore() {
        // AttachedTrait overrides this getter
        return this.actorStore_;
    }
    set actorStore(s) {
        this.actorStore_ = s;
    }
    get ownAspect() {
        return new Proxy(this, ensuredAspectProxy);
    }
    get context() {
        return new ActorContext(this);
    }
    get readableIdentifier() {
        return this.tid;
    }
    get exportingTreeNode() {
        return new BidderTreeNode(this);
    }
    get importingTreeNode() {
        return new ImportingTreeNode(this);
    }
    get exportingTree() {
        return new ExportingTree(this);
    }
    get importingTree() {
        return new ImportingTree(this);
    }
    get actorAspects() {
        return new ActorAspects(this);
    }
    get actorTraits() {
        return new ActorTraits(this);
    }
    get actorInfo() {
        return new ActorInfo(this);
    }
    get actorBundles() {
        return new ActorBundles(this);
    }
    get actorRenderer() {
        return new ActorRenderer(this);
    }
    get actorIntersect() {
        return new runtime.solidPackages.Rare.classes.ActorIntersect(this);
    }
    get actorMounted() {
        return new ActorMounted(this);
    }
    get actorEvents() {
        return new ActorEvents(this);
    }
    get actorContent() {
        return new ActorContent(this);
    }
    get actorMedia() {
        return new ActorMedia(this);
    }
    get actorHighlight() {
        return new runtime.solidPackages.Edit.classes.ActorHighlight(this);
    }
    get actorImport() {
        return new ActorImport(this);
    }
    get actorInteract() {
        return new ActorInteract(this);
    }
    get actorAudio() {
        return new ActorAudio(this);
    }
    get actorEdit() {
        return new runtime.solidPackages.Edit.classes.ActorEdit(this);
    }
    get actorStrip() {
        return new runtime.solidPackages.Edit.classes.ActorStrip(this);
    }
    get actorAttachedTraits() {
        return new ActorAttachedTraits(this);
    }
    get global() {
        return this.core.global;
    }
    get atsOf() {
        return this.global.atsOf;
    }
    get atOf() {
        return this.global.atOf;
    }
    get activesOf() {
        return this.global.activesOf;
    }
    get activeOf() {
        return this.global.activeOf;
    }
    get valuesOf() {
        return this.global.valuesOf;
    }
    get valueOf() {
        return this.global.valueOf;
    }
    get scaledValueOf() {
        return this.global.scaledValueOf;
    }
    get regional() {
        return this.core.regional;
    }
    get local() {
        return this.core.local;
    }
    /* IMPORTANT ASPECT-SHORTCUTS */
    get core() {
        return this.ownAspect.core;
    }
    get essence() {
        return this.ownAspect.essence;
    }
    get inner() {
        return this.ownAspect.inner;
    }
    get outer() {
        return this.ownAspect.outer;
    }
    get position() {
        return this.ownAspect.position;
    }
    /* IMPORTANT ACTOR-SHORTCUTS */
    get image() {
        return this.actorContent.regional.primaryOfKey('image');
    }
    get text() {
        return this.actorContent.regional.primaryOfKey('text');
    }
    get video() {
        return this.actorContent.regional.primaryOfKey('video');
    }
    get box() {
        return this.actorContent.regional.primaryOfKey('box');
    }
    get innerTextString() {
        return this.actorContent.local.firstTextBasis;
    }
    set innerTextString(s) {
        this.actorContent.local.firstTextBasis = s;
    }
    get persistentActor() {
        if (this instanceof runtime.class.AttachedTrait) {
            const at = this;
            if (at.trait)
                return at.trait;
        }
        return this;
    }
    get autoTags() {
        return [];
    }
    get tags() {
        return [...this.autoTags, ...this.actorStore.tags];
    }
    actorComponentDidMount(c) { }
    actorComponentWillUnmount(c) { }
    static get serializeInfo() {
        return {
            extends: Entity.serializeInfo,
            props: {
                actorStore: withDefault(object(ActorStore), {}, true),
            },
        };
    }
    static get stripInfo() {
        return {
            extends: Entity.stripInfo,
            props: {
                roles: strip((a) => a.actorStore.tags),
                contents: strip((a) => a.actorStrip.contents),
                audios: strip((a) => a.actorStrip.audios),
                aspects: strip((a) => a.actorStrip.aspects),
            },
        };
    }
}
__decorate([
    memoize
], Actor.prototype, "ownAspect", null);
__decorate([
    memoize
], Actor.prototype, "context", null);
__decorate([
    memoize
], Actor.prototype, "exportingTreeNode", null);
__decorate([
    memoize
], Actor.prototype, "importingTreeNode", null);
__decorate([
    memoize
], Actor.prototype, "exportingTree", null);
__decorate([
    memoize
], Actor.prototype, "importingTree", null);
__decorate([
    memoize
], Actor.prototype, "actorAspects", null);
__decorate([
    memoize
], Actor.prototype, "actorTraits", null);
__decorate([
    memoize
], Actor.prototype, "actorInfo", null);
__decorate([
    memoize
], Actor.prototype, "actorBundles", null);
__decorate([
    memoize
], Actor.prototype, "actorRenderer", null);
__decorate([
    memoize
], Actor.prototype, "actorIntersect", null);
__decorate([
    memoize
], Actor.prototype, "actorMounted", null);
__decorate([
    memoize
], Actor.prototype, "actorEvents", null);
__decorate([
    memoize
], Actor.prototype, "actorContent", null);
__decorate([
    memoize
], Actor.prototype, "actorMedia", null);
__decorate([
    memoize
], Actor.prototype, "actorHighlight", null);
__decorate([
    memoize
], Actor.prototype, "actorImport", null);
__decorate([
    memoize
], Actor.prototype, "actorInteract", null);
__decorate([
    memoize
], Actor.prototype, "actorAudio", null);
__decorate([
    memoize
], Actor.prototype, "actorEdit", null);
__decorate([
    memoize
], Actor.prototype, "actorStrip", null);
__decorate([
    memoize
], Actor.prototype, "actorAttachedTraits", null);
__decorate([
    memoize
], Actor.prototype, "atsOf", null);
__decorate([
    memoize
], Actor.prototype, "atOf", null);
__decorate([
    memoize
], Actor.prototype, "activesOf", null);
__decorate([
    memoize
], Actor.prototype, "activeOf", null);
__decorate([
    memoize
], Actor.prototype, "valuesOf", null);
__decorate([
    memoize
], Actor.prototype, "valueOf", null);
__decorate([
    memoize
], Actor.prototype, "scaledValueOf", null);
__decorate([
    memoize
], Actor.prototype, "regional", null);
__decorate([
    memoize
], Actor, "serializeInfo", null);
__decorate([
    memoize
], Actor, "stripInfo", null);
